import { Component } from '@theme/component';
import { SliderInitEvent, SliderSelectEvent, SliderScrollEvent } from '@theme/events';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} viewport – The Embla viewport element.
 * @property {HTMLElement} container – The Embla container element.
 * @property {HTMLElement[]} slides – The Embla slide elements.
 * @property {HTMLButtonElement} prevButton – The previous button.
 * @property {HTMLButtonElement} nextButton – The next button.
 * @property {HTMLElement} liveRegion – The live region element.
 * @property {HTMLElement} pagination – The pagination container.
 * @property {HTMLButtonElement[]} dots – The pagination dot buttons.
 */

/**
 * A reusable slider component using Embla Carousel.
 *
 * @extends Component<Refs>
 */
class SliderComponent extends Component {
  /** @type {EmblaApi | null} */
  #embla = null;
  /** @type {number | null} */
  #autoRotateIntervalId = null;
  /** @type {IntersectionObserver | null} */
  #intersectAutoScrollObserver = null;
  /** @type {number | null} */
  #intersectAutoScrollTimeoutId = null;
  /** @type {number | null} */
  #intersectAutoScrollIntervalId = null;
  #hasTriggeredIntersectAutoScroll = false;
  #isIntersectAutoScrollActive = false;
  #abortController = new AbortController();

  async connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const { signal } = this.#abortController;
    this.addEventListener('keydown', (e) => this.#handleKeyDown(e), { signal });
    this.#initEmbla();
    this.#initEditorEvents();
    this.#setupIntersectAutoScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#stopAutoRotate();
    this.#teardownIntersectAutoScroll();
    if (this.#embla) {
      this.#embla.destroy();
      this.#embla = null;
    }
  }

  #initEmbla() {
    if (this.#embla) {
      this.#stopAutoRotate();
      this.#embla.destroy();
      this.#embla = null;
    }

    const { viewport, container, prevButton, nextButton } = this.refs;
    if (!viewport || !container) return;

    // Vérifier que EmblaCarousel est disponible (chargé depuis le CDN)
    const EmblaCarousel = window.EmblaCarousel;
    if (typeof EmblaCarousel === 'undefined') {
      console.warn('EmblaCarousel is not loaded yet');
      return;
    }

    // Récupérer le plugin WheelGestures s'il est disponible
    const WheelGesturesPlugin = window.EmblaCarouselWheelGestures;

    const align = this.getAttribute('align') || 'start';
    const containScroll = this.getAttribute('contain-scroll') || 'trimSnaps';
    const dragFree = this.getAttribute('drag-free') === 'true';
    const slidesToScroll = parseInt(this.getAttribute('slides-to-scroll') || '1', 10);
    const loop = this.getAttribute('loop') === 'true';
    const breakpoints = this.#parseBreakpoints();

    // Default (mobile/below smallest breakpoint) active state
    const active = this.#getDefaultActive();

    const plugins = [];
    if (WheelGesturesPlugin) {
      plugins.push(WheelGesturesPlugin());
    }

    this.#embla = EmblaCarousel(viewport, {
      active,
      align,
      containScroll,
      dragFree,
      loop,
      slidesToScroll,
      breakpoints,
    }, plugins);

    this.#setupNavigation();
    this.#setupAccessibility();
    this.#setupPagination();
    this.#setupAutoRotate();

    // Dispatch custom events so parent components can react
    this.#embla.on('init', () => {
      this.dispatchEvent(new SliderInitEvent());
    });
    this.#embla.on('select', () => {
      if (!this.#embla) return;
      this.dispatchEvent(new SliderSelectEvent(this.#embla.selectedScrollSnap()));
    });
    this.#embla.on('scroll', () => {
      if (!this.#embla) return;
      const snaps = this.#embla.scrollSnapList();
      if (snaps.length <= 1) return;
      const progress = this.#embla.scrollProgress();
      const lastSnap = snaps[snaps.length - 1] ?? 0;
      const firstSnap = snaps[0] ?? 0;
      const gap = 1 - lastSnap;

      let visual;
      if (gap > 0 && progress > lastSnap) {
        // Loop gap: last → first — animate back to 0
        const t = (progress - lastSnap) / gap;
        visual = lastSnap * (1 - t);
      } else if (gap > 0 && progress < firstSnap) {
        // Loop gap: first → last — animate towards lastSnap
        const t = (firstSnap - progress) / gap;
        visual = lastSnap * t;
      } else {
        visual = progress;
      }

      this.dispatchEvent(new SliderScrollEvent(visual));
    });
  }

  #setupNavigation() {
    const { prevButton, nextButton } = this.refs;
    if (!prevButton || !nextButton || !this.#embla) return;

    const updateButtonStates = () => {
      if (!this.#embla) return;
      const canPrev = this.#embla.canScrollPrev();
      const canNext = this.#embla.canScrollNext();
      prevButton.disabled = !canPrev;
      nextButton.disabled = !canNext;
      prevButton.setAttribute('aria-disabled', String(!canPrev));
      nextButton.setAttribute('aria-disabled', String(!canNext));
    };

    this.#embla.on('init', updateButtonStates);
    this.#embla.on('select', updateButtonStates);
    this.#embla.on('reInit', updateButtonStates);
    updateButtonStates();
  }

  handlePrevClick() {
    if (this.#embla) {
      this.#embla.scrollPrev();
    }
  }

  handleNextClick() {
    if (this.#embla) {
      this.#embla.scrollNext();
    }
  }

  /**
   * Configure slide semantics and live announcements.
   */
  #setupAccessibility() {
    if (!this.#embla) return;

    const syncAccessibility = (announce = false) => {
      this.#updateSlidesAccessibility(announce);
    };

    this.#embla.on('init', () => syncAccessibility(false));
    this.#embla.on('reInit', () => syncAccessibility(false));
    this.#embla.on('select', () => syncAccessibility(true));
    this.#embla.on('settle', () => syncAccessibility(false));
    syncAccessibility(false);
  }

  /**
   * @param {boolean} announce
   */
  #updateSlidesAccessibility(announce) {
    const { container, slides, liveRegion } = this.refs;
    if (!container || !slides || !this.#embla) return;

    const slideElements = Array.isArray(slides) ? slides : [slides];
    const total = slideElements.length;
    const selectedIndex = this.#embla.selectedScrollSnap();
    const isCarouselActive = this.#isCarouselActive();

    this.#syncCarouselAttributes(isCarouselActive);

    slideElements.forEach((slide, index) => {
      if (isCarouselActive) {
        const isSelected = index === selectedIndex;
        const label = this.#getSlideLabel(index, total);

        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', this.getAttribute('data-carousel-roledescription') || 'slide');
        slide.setAttribute('aria-label', label);
        slide.setAttribute('aria-hidden', String(!isSelected));
      } else {
        slide.removeAttribute('role');
        slide.removeAttribute('aria-roledescription');
        slide.removeAttribute('aria-label');
        slide.removeAttribute('aria-hidden');
      }
    });

    if (liveRegion && announce && isCarouselActive) {
      this.#setLiveRegion(this.#getSlideLabel(selectedIndex, total));
    }
  }

  /**
   * @returns {boolean}
   */
  #isCarouselActive() {
    const breakpoints = this.#parseBreakpoints();
    let active = this.#getDefaultActive();

    for (const [query, options] of Object.entries(breakpoints)) {
      if (!window.matchMedia(query).matches) continue;
      if (typeof options?.active === 'boolean') {
        active = options.active;
      }
    }

    return active;
  }

  /**
   * Returns the default active state (below all breakpoints, i.e. mobile).
   * Uses the explicit `data-default-active` attribute when present,
   * otherwise defaults to `true`.
   *
   * @returns {boolean}
   */
  #getDefaultActive() {
    const attr = this.getAttribute('active-default');
    if (attr !== null) return attr !== 'false';
    return true;
  }

  /**
   * @param {boolean} isActive
   */
  #syncCarouselAttributes(isActive) {
    const roleDescription = this.getAttribute('data-carousel-roledescription') || 'carousel';
    const labelledBy = this.getAttribute('data-carousel-labelledby');
    const label = this.getAttribute('data-carousel-label');

    if (isActive) {
      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', roleDescription);
      this.setAttribute('tabindex', '0');

      if (labelledBy) {
        this.setAttribute('aria-labelledby', labelledBy);
        this.removeAttribute('aria-label');
      } else if (label) {
        this.setAttribute('aria-label', label);
        this.removeAttribute('aria-labelledby');
      } else {
        this.removeAttribute('aria-label');
        this.removeAttribute('aria-labelledby');
      }

      return;
    }

    this.removeAttribute('role');
    this.removeAttribute('aria-roledescription');
    this.removeAttribute('tabindex');
    this.removeAttribute('aria-label');
    this.removeAttribute('aria-labelledby');
  }

  /**
   * @param {number} index
   * @param {number} total
   * @returns {string}
   */
  #getSlideLabel(index, total) {
    const template = this.getAttribute('data-slide-label-template') || 'Slide [current] of [total]';

    return template
      .split('[current]').join(String(index + 1))
      .split('[total]').join(String(total));
  }

  /**
   * @param {string | undefined} text
   */
  #setLiveRegion(text) {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;

    liveRegion.textContent = text || '';

    window.setTimeout(() => {
      if (this.refs.liveRegion === liveRegion) {
        liveRegion.textContent = '';
      }
    }, 5000);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    if (!this.#embla) return;
    if (event.defaultPrevented) return;
    if (!this.#isCarouselActive()) return;

    const target = event.target;
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName))
    ) {
      return;
    }

    const lastIndex = this.#embla.scrollSnapList().length - 1;
    if (lastIndex < 1) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#embla.scrollPrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#embla.scrollNext();
        break;
      case 'Home':
        event.preventDefault();
        this.#embla.scrollTo(0);
        break;
      case 'End':
        event.preventDefault();
        this.#embla.scrollTo(lastIndex);
        break;
      default:
        break;
    }
  }

  #setupPagination() {
    const { dots } = this.refs;
    if (!dots || dots.length === 0 || !this.#embla) return;

    const activeClass = this.getAttribute('dot-active-class') || 'transformation-showcase__dot--active';

    const updateDots = () => {
      if (!this.#embla) return;
      const selectedIndex = this.#embla.selectedScrollSnap();

      dots.forEach((dot, index) => {
        dot.classList.toggle(activeClass, index === selectedIndex);
      });
    };

    this.#embla.on('init', updateDots);
    this.#embla.on('select', updateDots);
    this.#embla.on('reInit', updateDots);
    updateDots();
  }

  /** @param {number} index */
  handleDotClick(index) {
    if (this.#embla) {
      this.#embla.scrollTo(index);
    }
  }

  /**
   * Parse breakpoints from data-breakpoints attribute
   * @returns {Record<string, any>}
   */
  #parseBreakpoints() {
    const breakpointsAttr = this.getAttribute('data-breakpoints');
    if (!breakpointsAttr) return {};

    try {
      return JSON.parse(breakpointsAttr);
    } catch (error) {
      console.warn('Invalid breakpoints JSON:', error);
      return {};
    }
  }

  #setupAutoRotate() {
    this.#stopAutoRotate();
    if (!this.#embla) return;
    if (this.getAttribute('auto-rotate') !== 'true') return;

    const scrollSnaps = this.#embla.scrollSnapList();
    if (scrollSnaps.length <= 1) return;

    const interval = parseInt(this.getAttribute('auto-rotate-interval') || '5000', 10);
    const intervalMs = Number.isFinite(interval) && interval >= 1000 ? interval : 5000;

    this.#autoRotateIntervalId = window.setInterval(() => {
      if (!this.#embla || document.hidden) return;

      const lastIndex = this.#embla.scrollSnapList().length - 1;
      const currentIndex = this.#embla.selectedScrollSnap();

      if (currentIndex >= lastIndex) {
        this.#embla.scrollTo(0);
        return;
      }

      this.#embla.scrollNext();
    }, intervalMs);
  }

  /**
   * Stop auto-rotation timer
   */
  #stopAutoRotate() {
    if (this.#autoRotateIntervalId !== null) {
      window.clearInterval(this.#autoRotateIntervalId);
      this.#autoRotateIntervalId = null;
    }
  }

  #setupIntersectAutoScroll() {
    this.#teardownIntersectAutoScroll();
    this.#hasTriggeredIntersectAutoScroll = false;

    if (this.getAttribute('auto-scroll-on-intersect') !== 'true') return;
    if (window.Shopify?.designMode) return;

    const thresholdAttr = parseFloat(this.getAttribute('auto-scroll-on-intersect-threshold') || '0');
    const threshold = Number.isFinite(thresholdAttr) ? thresholdAttr : 0;
    const rootMargin =
      this.getAttribute('auto-scroll-on-intersect-root-margin') || '-45% 0px -45% 0px';

    this.#intersectAutoScrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.#isIntersectAutoScrollActive = entry.isIntersecting;

          if (entry.isIntersecting) {
            this.#triggerIntersectAutoScroll();
            return;
          }

          this.#stopIntersectAutoScrollLoop();
        });
      },
      {
        root: null,
        threshold,
        rootMargin,
      }
    );

    this.#intersectAutoScrollObserver.observe(this);
  }

  #teardownIntersectAutoScroll() {
    if (this.#intersectAutoScrollObserver) {
      this.#intersectAutoScrollObserver.disconnect();
      this.#intersectAutoScrollObserver = null;
    }

    if (this.#intersectAutoScrollTimeoutId !== null) {
      window.clearTimeout(this.#intersectAutoScrollTimeoutId);
      this.#intersectAutoScrollTimeoutId = null;
    }

    this.#stopIntersectAutoScrollLoop();
    this.#isIntersectAutoScrollActive = false;
  }

  #isIntersectAutoScrollRepeatEnabled() {
    return this.getAttribute('auto-scroll-on-intersect-repeat') === 'true';
  }

  #startIntersectAutoScrollLoop() {
    if (!this.#isIntersectAutoScrollRepeatEnabled()) return;
    if (this.#intersectAutoScrollIntervalId !== null) return;

    const intervalAttr = parseInt(
      this.getAttribute('auto-scroll-on-intersect-repeat-interval') || '1200',
      10
    );
    const intervalMs = Number.isFinite(intervalAttr) && intervalAttr >= 150 ? intervalAttr : 1200;

    this.#intersectAutoScrollIntervalId = window.setInterval(() => {
      if (!this.isConnected) return;
      if (!this.#isIntersectAutoScrollActive) return;
      if (document.hidden) return;

      this.#triggerIntersectAutoScroll();
    }, intervalMs);
  }

  #stopIntersectAutoScrollLoop() {
    if (this.#intersectAutoScrollIntervalId !== null) {
      window.clearInterval(this.#intersectAutoScrollIntervalId);
      this.#intersectAutoScrollIntervalId = null;
    }
  }

  #triggerIntersectAutoScroll() {
    const repeatEnabled = this.#isIntersectAutoScrollRepeatEnabled();
    const onceEnabled = !repeatEnabled && this.getAttribute('auto-scroll-on-intersect-once') !== 'false';

    if (this.#hasTriggeredIntersectAutoScroll && onceEnabled) {
      return;
    }

    if (this.getAttribute('auto-scroll-on-intersect-mobile-only') === 'true') {
      if (!window.matchMedia('(max-width: 989px)').matches) return;
    }

    const delayAttr = parseInt(this.getAttribute('auto-scroll-on-intersect-delay') || '0', 10);
    const delay = Number.isFinite(delayAttr) && delayAttr > 0 ? delayAttr : 0;
    const retryDelayAttr = parseInt(
      this.getAttribute('auto-scroll-on-intersect-retry-delay') || '180',
      10
    );
    const retryDelay = Number.isFinite(retryDelayAttr) && retryDelayAttr > 0 ? retryDelayAttr : 180;
    const maxRetriesAttr = parseInt(this.getAttribute('auto-scroll-on-intersect-max-retries') || '6', 10);
    const maxRetries = Number.isFinite(maxRetriesAttr) && maxRetriesAttr >= 0 ? maxRetriesAttr : 6;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const finalize = () => {
      this.#hasTriggeredIntersectAutoScroll = true;

      if (repeatEnabled) {
        this.#startIntersectAutoScrollLoop();
        return;
      }

      if (onceEnabled) {
        this.#intersectAutoScrollObserver?.disconnect();
        this.#intersectAutoScrollObserver = null;
      }
    };

    const attemptScroll = (attempt = 0) => {
      if (!this.isConnected) return;
      if (!this.#embla) {
        if (attempt < maxRetries) {
          this.#intersectAutoScrollTimeoutId = window.setTimeout(() => {
            this.#intersectAutoScrollTimeoutId = null;
            attemptScroll(attempt + 1);
          }, retryDelay);
        }
        return;
      }

      const scrollSnaps = this.#embla.scrollSnapList();
      const hasMultipleSnaps = Array.isArray(scrollSnaps) && scrollSnaps.length > 1;
      if (!hasMultipleSnaps) {
        if (attempt < maxRetries) {
          this.#intersectAutoScrollTimeoutId = window.setTimeout(() => {
            this.#intersectAutoScrollTimeoutId = null;
            attemptScroll(attempt + 1);
          }, retryDelay);
          return;
        }

        finalize();
        return;
      }

      const canScrollNext =
        typeof this.#embla.canScrollNext === 'function' ? this.#embla.canScrollNext() : true;

      if (!canScrollNext) {
        this.#stopIntersectAutoScrollLoop();
        finalize();
        return;
      }

      this.#embla.scrollNext();
      finalize();
    };

    if (delay === 0) {
      attemptScroll();
      return;
    }

    if (this.#intersectAutoScrollTimeoutId !== null) return;

    this.#intersectAutoScrollTimeoutId = window.setTimeout(() => {
      this.#intersectAutoScrollTimeoutId = null;
      attemptScroll();
    }, delay);
  }

  /**
   * Réinitialise le slider (utile après un morph)
   */
  reInit() {
    this.#initEmbla();
    this.#setupIntersectAutoScroll();
  }

  /**
   * Initialize Shopify theme editor events
   */
  #initEditorEvents() {
    if (!window.Shopify?.designMode) return;
    document.addEventListener('shopify:block:select', (e) => this.#handleBlockSelect(e), { signal: this.#abortController.signal });
  }

  /**
   * Handle block selection in theme editor
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    if (!this.#embla) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!this.contains(target)) return;

    const container = this.refs.container;
    if (!container || Array.isArray(container)) return;

    /** @type {HTMLElement | null} */
    let slide = target;
    while (slide && slide.parentElement !== container) {
      slide = slide.parentElement;
    }
    if (!slide) return;

    const slides = Array.from(container.children);
    const index = slides.indexOf(slide);
    if (index < 0) return;

    this.#embla.scrollTo(index);
  }
}

if (!customElements.get('slider-component')) {
  customElements.define('slider-component', SliderComponent);
}
